import React, {useCallback, useMemo, useRef, useState} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import vehicleService from "../../../../services/web/vehicle-service.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import { FaEraser, FaPlus, FaSearch } from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {MaskDefault} from "../../../../utils/mask.js";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";
const API_URL = import.meta.env.VITE_API_URL;

const initialData = {
    plate: "",
    brandModel: "",
    color: "",
    city: "",
}
const ReferenceVehicleList = () => {
    const navigate = navigateHook();
    const [state, setState] = useState(initialData)
    const [filter, setFilter] = useState([])
    const [isFiltered, setIsFiltered] = useState(false)
    const gridRef = useRef(null);
    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const columnDefs = [
        { headerName: "Placa", field: "plate", flex: 1, comparator: aggridUtils.nullLastComparator,
            valueFormatter: (params) => {
                if (!params.value) return "";

                return platePatternUtils.formatPlate(params.value);
            }
        },
        { headerName: "Marca/Modelo", field: "brandmodel", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Cor", field: "color", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Tipo (Detran)", field: "type", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Município - UF", field: "city", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Ano/Modelo", field: "modelyear", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Ocorrência(s) associada(s)", field: "hasoccurrence", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                let newA, newB
                a ? newA = "a" : newA = "b"
                b ? newB= "a" : newB= "b"
                return aggridUtils.nullLastComparator(newA, newB, nodeA, nodeB, isInverted);
            },
            cellRenderer: ({value}) => value ?
                <span title="Associado à ocorrência(s)">
                    <img className={"w-[30px] pt-0.5 mx-auto block h-[33px] cursor-pointer"} src={`${API_URL}/public/assets/img/sentry.entries.60x60.png`} alt={""}/>
                </span>
            : "",
        }
    ];

    const {data, isLoading} = useQuery({
        queryFn: () => vehicleService.list(filter).then(({data}) => {
            const vehicle = data.data

            vehicle.map(item => {
                const  plate = item.plate

                if (!plate) return "";

                const plateFormated = plate.replace(/[\s-]/g, "");

                item.plate = plateFormated.slice(0, 3) + "-" + plateFormated.slice(3);

                return item
            })

            return vehicle
        }),
        queryKey: ['vehicle', filter],
        enabled: !!isFiltered
    });

    const onChange = ({target: {id, value}}) => {
        if (id === "plate") value = platePatternUtils.formatPlate(value).toUpperCase();
        setState(prev => ({...prev, [id]: value}));
    };

    const apply = useCallback(() => {
        gridRef.current?.api?.showLoadingOverlay();

        setFilter([
            {
                'field': "reference.vehicles.plate",
                'value': state.plate.replace(/[^A-Z0-9]/g, "")
            },
            {
                'field': "reference.vehicles.data->>'brandModel'",
                'value': state.brandModel
            },
            {
                'field': "reference.vehicles.data->>'color'",
                'value': state.color
            },
            {
                'field': "reference.vehicles.data->>'city'",
                'value': state.city
            }
        ]);
        setIsFiltered(true)
    }, [state]);

    const clearFilter = useCallback(() => {
        setIsFiltered(false)
        setState(initialData);
        setFilter([]);
    }, []);

    const onCellClicked = ({data}) => {
        const isBlank = false;
        const urlDev = `/vehicle/form/${data.plate.replace("-", "").replaceAll(" ", "")}`;
        navigate(urlDev, isBlank);
    };

    const newVehicle = () => {
        const isBlank = false;
        const urlDev = `/vehicle/form`;
        navigate(urlDev, isBlank);
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Informações dos veículos</Template.Title>
                <Button onClick={newVehicle}>
                    <FaPlus/> Adicionar veículo
                </Button>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-3">
                                <InputForm id="plate" value={state.plate} onChange={onChange} label="Placa"/>
                            </div>
                            <div className="col-span-3">
                                <InputForm id="brandModel" value={state.brandModel} onChange={onChange}
                                           label="Marca/Modelo"/>
                            </div>
                            <div className="col-span-3">
                                <InputForm id="color" value={state.color} onChange={onChange}
                                           label="Cor"/>
                            </div>
                            <div className="flex col-span-3 gap-4">
                                <InputForm classNameWrapper={"w-full"} id="city" value={state.city} onChange={onChange}
                                           label="Município - UF"/>
                                <div className={"flex gap-2"}>
                                    <Button title="Limpar" onClick={() => clearFilter()} target={"primary-outline"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button title="Buscar" onClick={() => apply()}>
                                        <FaSearch/>
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        {
                            isFiltered ? (
                                <div className="flex">
                                    <div className="h-[calc(100vh-153px)] w-full p-0 ag-theme-quartz">
                                        <AgGridReact
                                            ref={gridRef}
                                            rowData={data ?? []}
                                            rowHeight={40}
                                            columnDefs={columnDefs}
                                            pagination
                                            paginationPageSize={100}
                                            paginationPageSizeSelector={[20, 50, 100, 500]}
                                            suppressNoRowsOverlay={isLoading}
                                            animateRows={false}
                                            suppressCellFocus
                                            onCellClicked={onCellClicked}
                                            gridOptions={{localeText: aggridUtils.localization()}}
                                            theme={theme}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    * Preencha um dos filtros antes de realizar a pesquisa.
                                </div>
                            )
                        }

                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ReferenceVehicleList;